import { useState, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom'
import { User, signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth'
import { auth, googleProvider } from './firebase'
import Login from './components/Login'
import Dashboard from './components/Dashboard'
import RoleManagement from './components/RoleManagement'
import Navbar from './components/Navbar'

// Component to handle redirect after login
function AuthCallback() {
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const redirectUrl = searchParams.get('redirect')
        const appId = searchParams.get('app')
        
        if (!redirectUrl) {
          setError('No redirect URL provided')
          setLoading(false)
          return
        }

        // Get current user
        const user = auth.currentUser
        if (!user) {
          setError('User not authenticated')
          setLoading(false)
          return
        }

        // Get ID token
        const idToken = await user.getIdToken()
        
        // Build redirect URL with token
        const url = new URL(redirectUrl)
        url.searchParams.set('token', idToken)
        url.searchParams.set('uid', user.uid)
        url.searchParams.set('email', user.email || '')
        url.searchParams.set('name', user.displayName || '')
        if (appId) {
          url.searchParams.set('app', appId)
        }

        // Redirect to the original app
        window.location.href = url.toString()
      } catch (error) {
        console.error('Callback error:', error)
        setError('Failed to process authentication callback')
        setLoading(false)
      }
    }

    handleCallback()
  }, [searchParams])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Completing authentication...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">Authentication Error</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.href = '/'}
            className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
          >
            Return to Login
          </button>
        </div>
      </div>
    )
  }

  return null
}

function App() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider)
    } catch (error) {
      console.error('Login error:', error)
    }
  }

  const handleLogout = async () => {
    try {
      await signOut(auth)
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  // Check if this is an integration request
  const redirectUrl = searchParams.get('redirect')
  const appId = searchParams.get('app')

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        {user && !redirectUrl && <Navbar user={user} onLogout={handleLogout} />}
        
        <div className="w-full px-4 py-8">
          <Routes>
            <Route 
              path="/callback" 
              element={<AuthCallback />} 
            />
            <Route 
              path="/" 
              element={
                user ? (
                  redirectUrl ? (
                    <Navigate to={`/callback?redirect=${encodeURIComponent(redirectUrl)}${appId ? `&app=${appId}` : ''}`} />
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                ) : (
                  <Login onLogin={handleLogin} appId={appId} />
                )
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                user ? <Dashboard user={user} /> : <Navigate to="/" />
              } 
            />
            <Route 
              path="/roles" 
              element={
                user ? <RoleManagement user={user} /> : <Navigate to="/" />
              } 
            />
          </Routes>
        </div>
      </div>
    </Router>
  )
}

export default App 